import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'
import { useQueries } from '@tanstack/react-query'

type CreditGrantV2 = Dashboardv99990101Api.CreditGrants.CreditGrantV2

// Grant ids come off the already-cached schedule GET, so this page can't drift
// from the Cube editor. The embedded spec has no balance or dates, so each
// grant's detail is fetched by id.
//
//   schedule.phases[].creditGrants[].id -> getCreditGrant(id) -> balance, dates
export const useScheduleCreditGrantDetails = ({
  scheduleId
}: {
  scheduleId: string
}): { grants: CreditGrantV2[] } => {
  const scheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    { id: scheduleId },
    { staleTime: 1 * MINUTES }
  )

  const grantIds = (scheduleQuery.data?.phases ?? []).flatMap(phase =>
    (phase.creditGrants ?? []).flatMap(grant => (grant.id ? [grant.id] : []))
  )

  // Detail queries reuse getCreditGrant's query key, sharing its cache so
  // invalidation hits them.
  const detailQueries = useQueries({
    queries: grantIds.map(id => ({
      queryKey: [...dashboardv99990101Client.getCreditGrant.queryKey, { id }],
      queryFn: async () => {
        const res = await dashboardv99990101Client.getCreditGrant.request({
          id
        })
        return res?.data ?? null
      },
      staleTime: 1 * MINUTES
    }))
  })

  const grants = detailQueries.flatMap(query =>
    query.data ? [query.data] : []
  )

  return { grants }
}
